// Pure time math shared by the pressure charts: bucket resolution for the
// multi-sensor trend overlay (WEB-1178) and timestamp/axis conversion for
// Plotly date axes (WEB-1218).

export const TARGET_POINTS = 700;

export const BUCKET_STEPS_MS = [
  60 * 1000,           // 1 min
  5 * 60 * 1000,       // 5 min
  15 * 60 * 1000,      // 15 min
  60 * 60 * 1000,      // 1 h
  3 * 60 * 60 * 1000,  // 3 h
  6 * 60 * 60 * 1000,  // 6 h
];

export const WAVEFORM_ZOOM_THRESHOLD_MS = 30 * 60 * 1000;

export const MAX_CUSTOM_DAYS = 92;

import { toEpochMs } from '../../../utils/timeParse';

export { toEpochMs };

const DAY_MS = 24 * 60 * 60 * 1000;

// Display offset for the global timezone selector, evaluated at the plotted
// instant so DST-season data keeps its own offset (same convention as
// formatCsvTimestamp, so charts match their CSV exports).
export const tzOffsetMs = (timeZone, projectTimezone, epochMs) => {
  if (timeZone === 'local') return -new Date(epochMs).getTimezoneOffset() * 60000;
  if (timeZone === 'project' && projectTimezone) {
    try {
      const date = new Date(epochMs);
      const utc = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
      const tz = new Date(date.toLocaleString('en-US', { timeZone: projectTimezone }));
      return tz.getTime() - utc.getTime();
    } catch {
      return 0;
    }
  }
  return 0;
};

/**
 * Convert a backend timestamp to a Plotly x value: a timezone-naive ISO
 * string at the selected timezone's wall clock. Plotly discards the 'Z' and
 * renders strings literally; Date objects (and bare numbers in trace data)
 * would instead be re-anchored to the BROWSER's timezone by plotly.js
 * dateTime2ms, which is exactly the WEB-1218 misalignment. Returns null
 * (a Plotly gap) for unparsable input.
 */
export const toPlotTime = (timeUtc, timeZone, projectTimezone) => {
  const epochMs = toEpochMs(timeUtc);
  if (isNaN(epochMs)) return null;
  return new Date(epochMs + tzOffsetMs(timeZone, projectTimezone, epochMs)).toISOString();
};

const readingTimeMs = (reading) => toEpochMs(reading.time_utc);

// TARGET_POINTS is a target, not a hard cap: a 10% overshoot tolerance lets
// natural bucket sizes win (30 days @ 1h = 720 points -> 1h, per the spec's
// "Month ≈ 1-h averages", instead of jumping to 3h).
const MAX_POINTS_WITH_TOLERANCE = TARGET_POINTS * 1.1;

export const pickBucketMs = (spanMs, pointsInSpan) => {
  if (!spanMs || spanMs <= 0) return null;
  if (!pointsInSpan || pointsInSpan <= TARGET_POINTS) return null;
  const step = BUCKET_STEPS_MS.find((s) => spanMs / s <= MAX_POINTS_WITH_TOLERANCE);
  return step || BUCKET_STEPS_MS[BUCKET_STEPS_MS.length - 1];
};

// aggregate: 'avg' (default) | 'min' | 'max' — which statistic each time
// bucket reports, mirroring RADAR's mean/min/max trend streams.
export const averageReadings = (readings, bucketMs, aggregate = 'avg') => {
  if (!bucketMs || !readings?.length) return readings || [];
  const numericTime = typeof readings[0].time_utc === 'number';
  const buckets = new Map();
  readings.forEach((r) => {
    const ms = readingTimeMs(r);
    const value = parseFloat(r.measurement_m);
    if (isNaN(ms) || isNaN(value)) return;
    const key = Math.floor(ms / bucketMs);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.sum += value;
      bucket.count += 1;
      if (value < bucket.min) bucket.min = value;
      if (value > bucket.max) bucket.max = value;
    } else {
      buckets.set(key, { sum: value, count: 1, min: value, max: value });
    }
  });
  return [...buckets.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([key, { sum, count, min, max }]) => {
      const centerMs = key * bucketMs + bucketMs / 2;
      let value = sum / count;
      if (aggregate === 'min') value = min;
      else if (aggregate === 'max') value = max;
      return {
        time_utc: numericTime ? centerMs : new Date(centerMs).toISOString(),
        measurement_m: value,
      };
    });
};

// Coarse everywhere, fine inside the visible window. The coarse bucket keeps
// the rangeslider (which mirrors the full traces) light; the fine bucket is
// the zoom "culling" behavior — deeper zoom, finer data.
export const buildDisplayReadings = (readings, visibleFromMs, visibleToMs, aggregate = 'avg') => {
  const list = (readings || [])
    .filter((r) => !isNaN(readingTimeMs(r)))
    .sort((a, b) => readingTimeMs(a) - readingTimeMs(b));
  if (!list.length) return [];

  const fullFrom = readingTimeMs(list[0]);
  const fullTo = readingTimeMs(list[list.length - 1]);
  const from = visibleFromMs != null ? visibleFromMs : fullFrom;
  const to = visibleToMs != null ? visibleToMs : fullTo;

  const coarseBucket = pickBucketMs(fullTo - fullFrom, list.length);
  const inWindow = list.filter((r) => {
    const ms = readingTimeMs(r);
    return ms >= from && ms <= to;
  });
  const fineBucket = pickBucketMs(to - from, inWindow.length);

  if (fineBucket === coarseBucket || (from <= fullFrom && to >= fullTo)) {
    return averageReadings(list, coarseBucket, aggregate);
  }

  const before = list.filter((r) => readingTimeMs(r) < from);
  const after = list.filter((r) => readingTimeMs(r) > to);
  return [
    ...averageReadings(before, coarseBucket, aggregate),
    ...averageReadings(inWindow, fineBucket, aggregate),
    ...averageReadings(after, coarseBucket, aggregate),
  ];
};

export const transientsInRange = (transients, fromMs, toMs) =>
  (transients || []).filter((tr) => {
    const ms = toEpochMs(tr.event_time);
    return !isNaN(ms) && ms >= fromMs && ms <= toMs;
  });

const toDateInput = (date) => date.toISOString().split('T')[0];

const MODE_DAYS = { day: 1, week: 7, month: 30 };

export const getModeRange = (mode, anchorDate, customFrom, customTo) => {
  if (mode === 'custom') {
    if (!customFrom || !customTo) return null;
    const from = new Date(customFrom);
    let to = new Date(customTo);
    if (isNaN(from.getTime()) || isNaN(to.getTime()) || to < from) return null;
    if ((to - from) / DAY_MS > MAX_CUSTOM_DAYS - 1) {
      to = new Date(from.getTime() + (MAX_CUSTOM_DAYS - 1) * DAY_MS);
    }
    return { startDate: toDateInput(from), endDate: toDateInput(to) };
  }
  const days = MODE_DAYS[mode] || 1;
  const end = new Date(anchorDate);
  if (isNaN(end.getTime())) return null;
  const start = new Date(end.getTime() - (days - 1) * DAY_MS);
  return { startDate: toDateInput(start), endDate: toDateInput(end) };
};
